/**
 * The EPUB export's status (decisions 7, 14; part 20, tables K and Y): under the toolbar while an export runs or has
 * something to say, never over the text, as the PDF's is. Its progress and Cancel; then the file to save, with the images
 * it couldn't take, or what went wrong with Try again.
 */
import type { EpubState } from '../epub/controller.ts';
import { focusOn } from './announce.ts';
import { el, when } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { JobProgress, JobStatus } from './job-status.ts';
import type { JobCount } from './job-status.ts';
import type { Shell } from './shell.ts';
import { component } from '../ui/solid.ts';
import Button from '../ui/Button.ts';

/** What the status says (K-rows' outcomes): reading, how far the documents have come, or putting the file together. */
export function epubText(shell: Shell, state: EpubState): string {
  const { t } = shell;
  switch (state.kind) {
    case 'idle':
      return '';
    case 'reading':
      return t('epub.reading');
    case 'exporting':
      return state.done >= state.total ? t('epub.finishing') : t('epub.progress', { percent: Math.floor((100 * state.done) / state.total) });
    case 'ready':
      return t('epub.ready');
    case 'failed':
      return state.reason === 'unreadable' ? t('epub.unreadable') : t('epub.failed');
  }
}

/** K13, Y4: the images a finished EPUB left out, in words, or nothing. */
function missingText(shell: Shell, state: EpubState): string {
  return state.kind === 'ready' && state.missing > 0 ? shell.t('epub.missing', { count: state.missing }) : '';
}

/**
 * Part 11c, A13: what the export announces: its phases (reading, making, finishing, ready, failed), never each percent the
 * status shows; a finished EPUB that left images out says so with it (Y4). Making starts before the first chapter.
 */
export function epubNews(shell: Shell, state: EpubState): string {
  if (state.kind === 'exporting' && (state.done < state.total || state.total === 0)) return shell.t('epub.making');
  return [epubText(shell, state), missingText(shell, state)].filter((line) => line !== '').join(' ');
}

/**
 * Y2, Y3: the status region, shown while the export isn't idle; what it announces is {@link epubNews}, said by the
 * workspace's announcers (A13). Each button leaves the focus where the next step is (A24): Cancel and Dismiss on the
 * toolbar's Export EPUB, Try again on the new Cancel.
 */
export function EpubStatus(shell: Shell): Child {
  const { t, epub } = shell;
  const state = (): EpubState => epub.state();
  const running = (): boolean => state().kind === 'reading' || state().kind === 'exporting';
  const count = (): JobCount => {
    const now = state();
    return now.kind === 'exporting' ? now : null;
  };
  return [
    when(
      () => state().kind !== 'idle',
      () =>
        JobStatus({
          class: 'epub-status',
          label: t('epub.label'),
          tone: () => (state().kind === 'failed' ? 'danger' : state().kind === 'ready' ? (missingText(shell, state()) === '' ? 'success' : 'warn') : 'neutral'),
          children: [
            el('p', {}, () => epubText(shell, state())),
            () => {
              const missing = missingText(shell, state());
              return missing === '' ? null : el('p', { class: 'epub-left-out' }, missing);
            },
            JobProgress(t('epub.label'), count),
          ],
          actions: [
            when(running, () =>
              component(Button, {
                size: 'sm',
                class: 'cancel-epub',
                onClick: () => {
                  epub.cancel();
                  focusOn(shell, '.export-epub');
                },
                children: t('epub.cancel'),
              }),
            ),
            () => {
              const now = state();
              return now.kind === 'ready' ? component(Button, { size: 'sm', class: 'save-epub', href: now.url, download: now.name, children: t('epub.save') }) : null;
            },
            when(
              () => state().kind === 'failed',
              () =>
                component(Button, {
                  size: 'sm',
                  onClick: () => {
                    epub.start();
                    focusOn(shell, '.cancel-epub');
                  },
                  children: t('epub.retry'),
                }),
            ),
            when(
              () => !running(),
              () =>
                component(Button, {
                  variant: 'ghost',
                  size: 'sm',
                  onClick: () => {
                    epub.dismiss();
                    focusOn(shell, '.export-epub');
                  },
                  children: t('epub.dismiss'),
                }),
            ),
          ],
        }),
    ),
  ];
}
